// Deterministic native-vs-fallback routing decision, extracted out of VideoDropTarget so it can
// be unit-tested without importing any React component (this project's Vitest/jsdom setup
// replaces `window`/`document` with minimal MobX/model-testing stubs — see vitest.setup.js — and
// does not support rendering or constructing React components).
import { t } from 'ttag'
import {
    compressVideoNative,
    isNativeCompressionSupported,
    NativeCompressionMetadata,
} from './NativeVideoTranscoder'

export interface NativeCompressionAttempt {
    succeeded: true
    file: File
    metadata: NativeCompressionMetadata
}

export interface NativeCompressionSkipped {
    succeeded: false
}

export type NativeCompressionOutcome = NativeCompressionAttempt | NativeCompressionSkipped

// Tries to compress `file` entirely in-browser via WebCodecs. Never contacts the external
// compressor. Returns `{ succeeded: false }` when native compression is unsupported for this
// file, or failed in a way eligible for fallback (native-unsupported, native-decode/encode/mux
// failure, or native size-retry exhaustion — see NativeVideoTranscoder.ts) — the caller should
// then fall back to the external compressor. `setProgressMessage` is called with a translated
// progress string while an attempt is in flight, and reset to '' when an attempt is abandoned.
export async function attemptNativeCompression(
    file: File,
    maxVideoSizeMB: number,
    preferredResolutionSetting: number,
    setProgressMessage: (message: string) => void,
): Promise<NativeCompressionOutcome> {
    try {
        // isNativeCompressionSupported only answers decode capability (demux + canDecode); it
        // does not depend on preferredResolutionSetting, since encoder capability for each
        // specific resolution/bitrate is checked per-attempt inside compressVideoNative, which
        // can advance past an unsupported resolution instead of aborting outright.
        const supported = await isNativeCompressionSupported(file)
        if (!supported) {
            return { succeeded: false }
        }

        setProgressMessage(t`Analyzing video...`)
        const { file: compressedFile, metadata } = await compressVideoNative(file, {
            maxVideoSizeMB,
            preferredResolution: preferredResolutionSetting,
            onProgress: (_pct, message) => setProgressMessage(message),
        })

        return { succeeded: true, file: compressedFile, metadata }
    } catch (error) {
        setProgressMessage('')
        return { succeeded: false }
    }
}
